import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Divider,
  Alert,
  Snackbar,
  InputAdornment,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useCompany } from "../../helpers/CompanyContext";
import {
  getNlisSettings,
  saveNlisSettings,
  COMPANY_TENANT_MAP,
} from "../../api/adminSettings";

const FEATURES = [
  { key: "erp_enquiry", label: "ERP Enquiry" },
  { key: "device_status", label: "Device Status" },
  { key: "view_holdings", label: "View Holdings" },
  { key: "erp_exceptions", label: "ERP Exceptions" },
  { key: "kill_uploads", label: "Kill Uploads" },
];

const emptyForm = {
  nlis_username: "",
  nlis_password: "",
  nlis_email: "",
  pic_number: "",
  est_number: "",
  nlis_environment: "testing",
  features: Object.fromEntries(FEATURES.map((f) => [f.key, false])),
};

const NlisSettings = () => {
  const { selectedCompany, companies, isAdmin } = useCompany();
  const tenant = COMPANY_TENANT_MAP[selectedCompany];
  const companyName =
    companies.find((c) => c.id === selectedCompany)?.name || tenant;

  const [form, setForm] = useState(emptyForm);
  const [hasPassword, setHasPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [isError, setIsError] = useState(false);
  const [showMessage, setShowMessage] = useState(false);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getNlisSettings(tenant);
      setForm({
        nlis_username: data.nlis_username || "",
        nlis_password: "", // never populated from server
        nlis_email: data.nlis_email || "",
        pic_number: data.pic_number || "",
        est_number: data.est_number || "",
        nlis_environment: data.nlis_environment || "testing",
        features: { ...emptyForm.features, ...data.features },
      });
      setHasPassword(data.has_password);
    } catch (err) {
      setIsError(true);
      setMessage("Failed to load NLIS settings");
      setShowMessage(true);
    } finally {
      setLoading(false);
    }
  }, [tenant]);

  useEffect(() => {
    if (tenant) loadSettings();
  }, [tenant, loadSettings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        nlis_username: form.nlis_username,
        nlis_email: form.nlis_email,
        pic_number: form.pic_number,
        est_number: form.est_number,
        nlis_environment: form.nlis_environment,
        features: form.features,
      };
      // only send password if admin typed a new one
      if (form.nlis_password) payload.nlis_password = form.nlis_password;

      const { data } = await saveNlisSettings(tenant, payload);
      setHasPassword(data.has_password);
      setForm((f) => ({ ...f, nlis_password: "" }));
      setIsError(false);
      setMessage("Settings saved");
      setShowMessage(true);
    } catch (err) {
      setIsError(true);
      setMessage(err.response?.data?.error || "Failed to save settings");
      setShowMessage(true);
    } finally {
      setSaving(false);
    }
  };

  if (!tenant) return <Alert severity="warning">Unknown company selected.</Alert>;

  return (
    <Box sx={{ maxWidth: 560, mx: "auto", p: 2 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          NLIS Settings — {companyName}
        </Typography>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Typography variant="subtitle2" sx={{ mt: 1 }}>
              NLIS Login Details
            </Typography>
            <TextField
              label="NLIS Username"
              fullWidth
              margin="normal"
              value={form.nlis_username}
              onChange={(e) =>
                setForm({ ...form, nlis_username: e.target.value })
              }
            />
            <TextField
              label="NLIS Password"
              fullWidth
              margin="normal"
              type={showPassword ? "text" : "password"}
              value={form.nlis_password}
              placeholder={hasPassword ? "•••••••• (saved — leave blank to keep)" : ""}
              onChange={(e) =>
                setForm({ ...form, nlis_password: e.target.value })
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="NLIS Email"
              type="email"
              fullWidth
              margin="normal"
              value={form.nlis_email}
              onChange={(e) =>
                setForm({ ...form, nlis_email: e.target.value })
              }
            />
            <TextField
              label="PIC Number"
              fullWidth
              margin="normal"
              value={form.pic_number}
              onChange={(e) =>
                setForm({ ...form, pic_number: e.target.value })
              }
            />
            <TextField
              label="EST Number"
              fullWidth
              margin="normal"
              value={form.est_number}
              onChange={(e) =>
                setForm({ ...form, est_number: e.target.value })
              }
            />

            {isAdmin && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2">NLIS Environment</Typography>
                <FormControl>
                  <FormLabel id="nlis-environment-label" sx={{ display: "none" }}>
                    NLIS Environment
                  </FormLabel>
                  <RadioGroup
                    row
                    aria-labelledby="nlis-environment-label"
                    value={form.nlis_environment}
                    onChange={(e) =>
                      setForm({ ...form, nlis_environment: e.target.value })
                    }
                  >
                    <FormControlLabel
                      value="testing"
                      control={<Radio />}
                      label="Testing URL"
                    />
                    <FormControlLabel
                      value="production"
                      control={<Radio />}
                      label="Production URL"
                    />
                  </RadioGroup>
                </FormControl>
              </>
            )}

            {isAdmin && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2">Enabled Features</Typography>
                <FormGroup>
                  {FEATURES.map((f) => (
                    <FormControlLabel
                      key={f.key}
                      control={
                        <Checkbox
                          checked={form.features[f.key]}
                          onChange={(e) =>
                            setForm({
                              ...form,
                              features: {
                                ...form.features,
                                [f.key]: e.target.checked,
                              },
                            })
                          }
                        />
                      }
                      label={f.label}
                    />
                  ))}
                </FormGroup>
              </>
            )}

            <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
              <Button variant="contained" onClick={handleSave} disabled={saving}>
                {saving ? "Saving..." : "Save"}
              </Button>
            </Box>
          </>
        )}
      </Paper>

      <Snackbar
        open={showMessage}
        autoHideDuration={4000}
        onClose={() => setShowMessage(false)}
      >
        <Alert severity={isError ? "error" : "success"} onClose={() => setShowMessage(false)}>
          {message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NlisSettings;